<template>
    <video
        v-if="resolved?.kind === 'video'"
        ref="videoElement"
        :src="resolved.videoSrc"
        :class="cssClass"
        :poster="poster"
        :loop="loop"
        :autoplay="autoplay"
        :muted="muted"
        :controls="controls"
        :preload="preload"
        playsinline
    />
    <ResponsiveImage
        v-else-if="resolved?.kind === 'image'"
        :image-id="resolved.imageId"
        :alt="alt"
        :sizes="sizes"
        :priority="priority"
        :loading="loading"
        :css-class="cssClass"
    />
</template>

<script setup lang="ts">
import ResponsiveImage from '#layers/cms/app/components/ResponsiveImage.vue'
import type { ApiResponse } from '#layers/cms/shared/types/ApiResponse'
import type { ImageAltText } from '#layers/cms/shared/types/ImageBucket'
import type { MediaKind } from '#layers/cms/shared/types/MediaBucket'

// Two ways to use this component:
// - `mediaId` (a b-media pointer contentId): kind is unknown up front, so it's resolved via a
//   metadata fetch (same call DuoParalaxImages.vue already makes), and the *real* underlying
//   b-image id is used for the image path so ResponsiveImage's own responsive/srcset/crop-center
//   logic keeps working.
// - `kind` + `assetId`: what legacy image-specific/video-specific components pass, since they
//   know their field's kind statically. `assetId` predates b-media for content nobody has
//   touched since, so it's normally a raw b-image/b-video id — BUT the CMS's media picker widget
//   always uploads through /api/files/media/save, which always creates a b-media pointer
//   regardless of the field's own declared kind. So the instant someone re-picks/re-uploads a
//   legacy field's value through the current CMS, its stored id becomes a b-media pointer too,
//   even though the field is still typed as plain image/video. `assetId` therefore has to be
//   probed the same way `mediaId` is, falling back to the raw direct URL only if that fails.
const props = defineProps<{
    mediaId?: string
    kind?: MediaKind
    assetId?: string
    cssClass?: string | string[] | Record<string, boolean>
    alt?: ImageAltText
    sizes?: string
    priority?: boolean
    loading?: 'lazy' | 'eager'
    poster?: string
    autoplay?: boolean
    loop?: boolean
    muted?: boolean
    controls?: boolean
    preload?: 'none' | 'metadata' | 'auto'
    // Imperative play/pause, for callers that need to start/stop a video that's already mounted
    // (e.g. a carousel slide that should only play while centered) — the `autoplay` attribute
    // alone only affects the initial load, not later toggles. Leave unset to just use `autoplay`.
    playing?: boolean
}>()

type Resolved = { kind: 'video', videoSrc: string } | { kind: 'image', imageId: string } | null

const resolved = ref<Resolved>(null)
const videoElement = ref<HTMLVideoElement>()

let loadSequence = 0

// Tries `id` as a b-media pointer id. Returns the resolved media on success, or `null` if it
// isn't one (404) — the caller decides what "not a pointer" should fall back to.
const resolveAsMediaPointer = async (id: string): Promise<Resolved> => {
    try {
        const response = await $fetch<ApiResponse<{ kind: MediaKind, refContentId: string }>>(
            `/api/files/media/${ id }/metadata`,
        )
        if (!response.success) return null
        return response.data.kind === 'video'
            // Stream from the video endpoint directly via the resolved b-video id: the metadata
            // endpoint also accepts raw b-video ids, which /api/files/media/:id would 404 on.
            ? { kind: 'video', videoSrc: `/api/files/video/${ response.data.refContentId }` }
            : { kind: 'image', imageId: response.data.refContentId }
    } catch {
        return null
    }
}

const resolveMedia = async () => {
    const sequence = ++loadSequence

    if (props.kind && props.assetId) {
        const asAssetId = props.assetId
        const viaPointer = await resolveAsMediaPointer(asAssetId)
        if (sequence !== loadSequence) return
        resolved.value = viaPointer ?? (props.kind === 'video'
            ? { kind: 'video', videoSrc: `/api/files/video/${ asAssetId }` }
            : { kind: 'image', imageId: asAssetId })
        return
    }
    if (props.kind) {
        resolved.value = null
        return
    }
    if (!props.mediaId) {
        resolved.value = null
        return
    }

    const viaPointer = await resolveAsMediaPointer(props.mediaId)
    if (sequence !== loadSequence) return
    if (!viaPointer) console.error(`Could not resolve media ${ props.mediaId }`)
    resolved.value = viaPointer
}

watch(() => [props.kind, props.assetId, props.mediaId], resolveMedia, { immediate: true })

// The `autoplay` attribute only reliably kicks in for elements present at mount time. When
// resolving via `mediaId`, the metadata fetch means the <video> is actually created on a later
// update (after mount), not as part of it — so `autoplay` alone can silently fail to start
// playback. Driving playback imperatively once the element exists sidesteps that regardless of
// when the element was inserted. `playing` (explicit, e.g. a carousel slide's centered state)
// takes precedence over `autoplay` (implicit "play as soon as ready") when both are set.
const effectivePlaying = computed(() => props.playing ?? props.autoplay)

watch(
    [effectivePlaying, videoElement, () => resolved.value?.kind],
    ([playing, element, kind]) => {
        if (playing === undefined || kind !== 'video' || !element) return
        if (playing) element.play().catch(err => console.error('Could not autoplay video', resolved.value, err))
        else element.pause()
    },
)
</script>
